import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  CampaignSummary,
  NewsletterBlock,
  NewsletterTemplate,
  withBlockIds,
} from "../../../model/NewsletterCampaign";
import {
  createNewsletterTemplate,
  deleteNewsletterTemplate,
  listNewsletterTemplates,
  listNiposomCampaigns,
  newsletterErrorMessage,
  updateNewsletterTemplate,
} from "../../../helpers/newsletterCampaignsApi";
import NewsletterBlockEditor from "./NewsletterBlockEditor";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const buttonPrimary =
  "inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50";
const buttonSecondary =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";

const NewsletterTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<NewsletterTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);

  const [editing, setEditing] = useState<NewsletterTemplate | null>(null);
  const [name, setName] = useState("");
  const [blocks, setBlocks] = useState<NewsletterBlock[]>([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const loadCampaigns = useCallback(async () => {
    try {
      setCampaigns(await listNiposomCampaigns());
      setCampaignsError(null);
    } catch {
      setCampaigns([]);
      setCampaignsError(
        "Campanhas indisponíveis — verifica a ligação ao catálogo Niposom.",
      );
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await listNewsletterTemplates());
      void loadCampaigns();
    } catch (e: any) {
      setError(newsletterErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [loadCampaigns]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing({
      id: "",
      tenant_id: "",
      name: "",
      slug: "",
      blocks: [],
      is_system: false,
      created_at: "",
      updated_at: "",
    });
    setName("");
    setBlocks([]);
  };

  const openEdit = (template: NewsletterTemplate) => {
    setEditing(template);
    setName(template.name);
    setBlocks(withBlockIds(JSON.parse(JSON.stringify(template.blocks || []))));
  };

  const run = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e: any) {
      setError(newsletterErrorMessage(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      if (!editing) return;
      if (editing.id) {
        await updateNewsletterTemplate(editing.id, {
          name,
          blocks,
          html_source: editing.html_source || "",
        });
      } else {
        await createNewsletterTemplate({ name, blocks, html_source: "" });
      }
      setSuccess("Template guardado.");
      setEditing(null);
      await load();
    });

  const remove = (template: NewsletterTemplate) =>
    run(async () => {
      if (!confirm(`Eliminar o template "${template.name}"?`)) return;
      await deleteNewsletterTemplate(template.id);
      setSuccess("Template eliminado.");
      await load();
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Templates</h2>
          <p className="text-sm text-gray-500">
            Templates base prontos a usar e templates personalizados.
          </p>
        </div>
        <button type="button" className={buttonPrimary} onClick={openNew}>
          <Plus size={16} /> Novo template
        </button>
      </div>

      <div aria-live="polite">
        {success && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            <CheckCircle size={16} /> {success}
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle size={16} /> {error}
          </div>
        )}
        {campaignsError && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <AlertCircle size={16} /> {campaignsError}
            <button
              type="button"
              className="ml-auto rounded border border-amber-300 px-2 py-1 text-xs"
              onClick={() => loadCampaigns()}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      {!editing && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full py-8 text-center text-gray-500">
              <Loader2 className="mx-auto animate-spin" size={20} />
            </div>
          ) : (
            templates.map((template) => (
              <div
                key={template.id}
                className="rounded-lg border border-gray-200 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-800">{template.name}</p>
                    <p className="text-xs text-gray-500">
                      {template.is_system
                        ? "Template base (editável)"
                        : "Personalizado"}{" "}
                      · {(template.blocks || []).length} bloco(s)
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                      onClick={() => openEdit(template)}
                      title="Editar"
                      aria-label={`Editar ${template.name}`}
                      disabled={busy}
                    >
                      <Pencil size={16} />
                    </button>
                    {!template.is_system && (
                      <button
                        type="button"
                        className="rounded p-1.5 text-red-600 hover:bg-gray-100"
                        onClick={() => remove(template)}
                        title="Eliminar"
                        aria-label={`Eliminar ${template.name}`}
                        disabled={busy}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {editing && (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-semibold text-gray-800">
              {editing.id ? "Editar template" : "Novo template"}
            </h3>
            <button
              type="button"
              className={buttonSecondary}
              onClick={() => setEditing(null)}
            >
              <X size={16} /> Fechar
            </button>
          </div>
          <input
            className={inputClass}
            placeholder="Nome do template"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <NewsletterBlockEditor
            blocks={blocks}
            onChange={setBlocks}
            campaigns={campaigns}
          />
          <button
            type="button"
            className={buttonPrimary}
            onClick={save}
            disabled={busy}
          >
            Guardar template
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsletterTemplates;
